/**
 * ImportFromGithubModal
 *
 * Scans the project's connected GitHub repo for markdown documents and lets the
 * user bulk-import selected files into Flowicity. Each row exposes an editable
 * doc_type, file size, preview snippet, and an "already imported" indicator.
 */

import { Component, createSignal, createEffect, For, Show } from "solid-js";
import { Modal, Button, Select, Spinner } from "../ui";
import {
  scanRepoDocuments,
  importDocuments,
  type ProjectId,
  type ScannedDocument,
  type ImportDocumentsResponse,
  type DocumentType,
} from "../../api";

type Step = "loading" | "select" | "importing" | "done" | "error";

const DOC_TYPE_OPTIONS = [
  { value: "roadmap", label: "Roadmap" },
  { value: "architecture", label: "Architecture" },
  { value: "adr", label: "ADR" },
  { value: "design", label: "Design" },
  { value: "guide", label: "Guide" },
  { value: "custom", label: "Custom" },
];

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export interface ImportFromGithubModalProps {
  open: boolean;
  onClose: () => void;
  projectId: ProjectId;
  onImportComplete: () => void;
}

export const ImportFromGithubModal: Component<ImportFromGithubModalProps> = (
  props,
) => {
  const [step, setStep] = createSignal<Step>("loading");
  const [items, setItems] = createSignal<ScannedDocument[]>([]);
  const [selectedPaths, setSelectedPaths] = createSignal<Set<string>>(
    new Set(),
  );
  const [typeOverrides, setTypeOverrides] = createSignal<
    Record<string, DocumentType>
  >({});
  const [errorMsg, setErrorMsg] = createSignal<string>("");
  const [result, setResult] = createSignal<ImportDocumentsResponse | null>(
    null,
  );

  const scan = async () => {
    setStep("loading");
    setErrorMsg("");
    try {
      const resp = await scanRepoDocuments(props.projectId);
      setItems(resp.items);
      const defaults = new Set<string>();
      for (const it of resp.items) {
        if (!it.already_imported) defaults.add(it.path);
      }
      setSelectedPaths(defaults);
      setTypeOverrides({});
      setStep("select");
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Failed to scan repository.",
      );
      setStep("error");
    }
  };

  let lastOpen = false;
  createEffect(() => {
    if (props.open && !lastOpen) {
      lastOpen = true;
      scan();
    } else if (!props.open) {
      lastOpen = false;
    }
  });

  const togglePath = (path: string) => {
    const next = new Set(selectedPaths());
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setSelectedPaths(next);
  };

  const allSelectableSelected = () => {
    const sel = selectedPaths();
    const all = items();
    if (all.length === 0) return false;
    return all.every((it) => sel.has(it.path));
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected()) {
      setSelectedPaths(new Set<string>());
    } else {
      setSelectedPaths(new Set(items().map((it) => it.path)));
    }
  };

  const setOverride = (path: string, docType: DocumentType) => {
    setTypeOverrides({ ...typeOverrides(), [path]: docType });
  };

  const effectiveDocType = (it: ScannedDocument): DocumentType => {
    return typeOverrides()[it.path] ?? it.doc_type;
  };

  const handleImport = async () => {
    const paths = Array.from(selectedPaths());
    if (paths.length === 0) return;
    setStep("importing");
    setErrorMsg("");
    try {
      const overrides: Record<string, DocumentType> = {};
      for (const p of paths) {
        const it = items().find((x) => x.path === p);
        if (!it) continue;
        const chosen = effectiveDocType(it);
        // Only send override when the user changed the inferred type
        if (chosen !== it.doc_type) overrides[p] = chosen;
      }
      const resp = await importDocuments(props.projectId, {
        paths,
        doc_type_overrides: overrides,
      });
      setResult(resp);
      setStep("done");
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Failed to import documents.",
      );
      setStep("error");
    }
  };

  const handleClose = () => {
    const r = result();
    if (r && r.imported + r.updated > 0) {
      props.onImportComplete();
    }
    props.onClose();
    // Reset for next open
    setStep("loading");
    setItems([]);
    setSelectedPaths(new Set<string>());
    setTypeOverrides({});
    setErrorMsg("");
    setResult(null);
  };

  const handleDone = () => {
    handleClose();
  };

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      title="Import Documents from GitHub"
      size="xl"
    >
      <div>
        {/* Loading state */}
        <Show when={step() === "loading"}>
          <div class="flex items-center justify-center gap-2 py-12">
            <Spinner size="md" />
            <span class="text-sm text-ink-2">
              Scanning repository for documents…
            </span>
          </div>
        </Show>

        {/* Error state */}
        <Show when={step() === "error"}>
          <div class="py-4">
            <div class="bg-signal-red-tint border border-signal-red/25 rounded p-3 text-sm text-signal-red mb-4">
              {errorMsg() || "Something went wrong."}
            </div>
            <div class="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={handleClose}>
                Close
              </Button>
              <Button variant="primary" size="sm" onClick={scan}>
                Retry
              </Button>
            </div>
          </div>
        </Show>

        {/* Select state */}
        <Show when={step() === "select"}>
          <Show
            when={items().length > 0}
            fallback={
              <div class="py-8 text-center">
                <p class="text-sm text-ink-2">
                  No importable markdown documents were found in the connected
                  repository.
                </p>
                <div class="mt-4 flex justify-center gap-2">
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    Close
                  </Button>
                  <Button variant="primary" size="sm" onClick={scan}>
                    Rescan
                  </Button>
                </div>
              </div>
            }
          >
            <div class="flex items-center justify-between mb-3">
              <label class="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelectableSelected()}
                  onChange={toggleSelectAll}
                  class="w-4 h-4 accent-ink rounded border-line"
                />
                <span>
                  Select all ({selectedPaths().size}/{items().length})
                </span>
              </label>
              <span class="text-xs text-ink-3">
                {items().length} document{items().length === 1 ? "" : "s"} found
              </span>
            </div>

            <div class="border border-line rounded divide-y divide-line max-h-[50vh] overflow-y-auto">
              <For each={items()}>
                {(it) => (
                  <div class="flex items-start gap-3 px-3 py-3 hover:bg-sunken">
                    <input
                      type="checkbox"
                      checked={selectedPaths().has(it.path)}
                      onChange={() => togglePath(it.path)}
                      class="mt-1 w-4 h-4 accent-ink rounded border-line flex-shrink-0"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-sm font-medium text-ink break-all">
                          {it.path}
                        </span>
                        <Show when={it.already_imported}>
                          <span class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-sunken text-ink-2 border border-line">
                            Already imported
                          </span>
                        </Show>
                        <span class="text-xs text-ink-3">
                          {formatSize(it.size)}
                        </span>
                      </div>
                      <Show when={it.preview}>
                        <p class="text-xs text-ink-3 mt-1 line-clamp-2">
                          {it.preview}
                        </p>
                      </Show>
                    </div>
                    <div class="w-36 flex-shrink-0">
                      <Select
                        value={effectiveDocType(it)}
                        onChange={(v) =>
                          setOverride(it.path, v as DocumentType)
                        }
                        options={DOC_TYPE_OPTIONS}
                      />
                    </div>
                  </div>
                )}
              </For>
            </div>

            <div class="mt-4 flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleImport}
                disabled={selectedPaths().size === 0}
              >
                Import {selectedPaths().size} document
                {selectedPaths().size === 1 ? "" : "s"}
              </Button>
            </div>
          </Show>
        </Show>

        {/* Importing state */}
        <Show when={step() === "importing"}>
          <div class="flex items-center justify-center gap-2 py-12">
            <Spinner size="md" />
            <span class="text-sm text-ink-2">
              Importing {selectedPaths().size} document
              {selectedPaths().size === 1 ? "" : "s"}…
            </span>
          </div>
        </Show>

        {/* Done state */}
        <Show when={step() === "done" ? result() : null}>
          {(r) => (
            <div class="py-2">
              <div class="mb-4 flex items-center gap-4 text-sm">
                <span class="inline-flex items-center gap-1 text-signal-green">
                  <span class="font-semibold">{r().imported}</span> created
                </span>
                <span class="inline-flex items-center gap-1 text-signal-blue">
                  <span class="font-semibold">{r().updated}</span> updated
                </span>
                <Show when={r().failed > 0}>
                  <span class="inline-flex items-center gap-1 text-signal-red">
                    <span class="font-semibold">{r().failed}</span> failed
                  </span>
                </Show>
              </div>

              <div class="border border-line rounded divide-y divide-line max-h-[40vh] overflow-y-auto">
                <For each={r().results}>
                  {(res) => (
                    <div class="flex items-center justify-between gap-3 px-3 py-2">
                      <span class="text-sm text-ink-2 truncate">
                        {res.path}
                      </span>
                      <Show when={res.status === "created"}>
                        <span class="text-xs font-medium text-signal-green">
                          Created
                        </span>
                      </Show>
                      <Show when={res.status === "updated"}>
                        <span class="text-xs font-medium text-signal-blue">
                          Updated
                        </span>
                      </Show>
                      <Show when={res.status === "failed"}>
                        <span
                          class="text-xs font-medium text-signal-red"
                          title={res.error ?? ""}
                        >
                          Failed
                        </span>
                      </Show>
                    </div>
                  )}
                </For>
              </div>

              <div class="mt-4 flex justify-end">
                <Button variant="primary" size="sm" onClick={handleDone}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Show>
      </div>
    </Modal>
  );
};
